import { useState } from "react";

import {
  Button,
  DataList,
  ErrorState,
  Field,
  Page,
  Panel,
  StatusBadge,
  Toolbar,
} from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import { usePublishArtifact } from "../lib/queries";

import { ApiError } from "@/lib/api";

interface Entry {
  readonly id: string;
  readonly where: string;
  readonly detail: string;
}

function entriesOf(error: unknown): Entry[] {
  if (!(error instanceof ApiError)) return [];
  return Object.entries(error.detail).map(([where, detail]) => ({
    id: where,
    where,
    detail: String(detail),
  }));
}

function reportText(error: ApiError, entries: readonly Entry[]): string {
  const head = `${error.code}: ${error.message}`;
  return [head, ...entries.map((one) => `${one.where}  ${one.detail}`)].join("\n");
}

/** The outcome of one publish: what it minted, or every refusal it named. */
export function ValidationReport() {
  const [file, setFile] = useState<File | null>(null);
  const publish = usePublishArtifact();

  const error = publish.error instanceof ApiError ? publish.error : null;
  const entries = entriesOf(publish.error);

  return (
    <Page title="Validation" eyebrow="Publisher">
      <Panel title="Artifact">
        <Toolbar fill>
          <Field label="Package">
            {(render) => (
              <input
                id={render.id}
                className="al-control pb-file"
                type="file"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                }}
              />
            )}
          </Field>
          <Button
            variant="primary"
            disabled={file === null}
            loading={publish.isPending}
            onClick={() => {
              if (file !== null) publish.mutate(file);
            }}
          >
            Publish
          </Button>
        </Toolbar>
      </Panel>

      {publish.isSuccess ? (
        <Panel title="Published" action={<StatusBadge tone="success">published</StatusBadge>}>
          <DataList
            items={[
              {
                id: publish.data.activity_id,
                title: publish.data.activity_id,
                fields: [{ label: "Version", value: publish.data.version, mono: true }],
              },
            ]}
          />
        </Panel>
      ) : null}

      {error === null ? null : (
        <Panel
          title="Refusals"
          subtitle={error.code}
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(reportText(error, entries));
              }}
            >
              Copy report
            </Button>
          }
        >
          {entries.length === 0 ? (
            <ErrorState headline={error.message} code={String(error.status)} />
          ) : (
            <PagedTable
              rows={entries}
              pageSize={15}
              caption="Refusals"
              emptyHeadline="Nothing refused"
              placeholder="Find a refusal"
              match={(row, needle) => `${row.where} ${row.detail}`.toLowerCase().includes(needle)}
              columns={[
                { key: "where", header: "Where", emphasis: true, mono: true },
                { key: "detail", header: "Detail" },
              ]}
            />
          )}
        </Panel>
      )}
    </Page>
  );
}
